import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Truck, Package, Search, ChevronRight, Clock, 
  MapPin, FileText, CheckCircle, AlertCircle 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminShipping = () => {
  const { getAuthHeaders } = useAdmin();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [carrierFilter, setCarrierFilter] = useState('');

  // Create label form
  const [showCreateLabel, setShowCreateLabel] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [labelForm, setLabelForm] = useState({
    carrier: 'usps',
    service_level: 'ground',
    package_weight: 1.0
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchShipments();
  }, [page, statusFilter, carrierFilter]);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      let url = `${API}/shipping/list?page=${page}&limit=20`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (carrierFilter) url += `&carrier=${carrierFilter}`;
      
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setShipments(response.data.shipments || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching shipments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLabel = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setCreating(true);

    try {
      const response = await axios.post(`${API}/shipping/create-label`, {
        order_id: selectedOrderId,
        ...labelForm
      }, { headers: getAuthHeaders() });
      
      setSuccess(`Label created! Tracking: ${response.data.tracking_number}`);
      setShowCreateLabel(false);
      fetchShipments();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create label');
    } finally {
      setCreating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      label_created: 'bg-blue-100 text-blue-800',
      picked_up: 'bg-indigo-100 text-indigo-800',
      in_transit: 'bg-purple-100 text-purple-800',
      out_for_delivery: 'bg-orange-100 text-orange-800',
      delivered: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-stone-100 text-stone-800';
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6" data-testid="admin-shipping">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-stone-800">Shipping Management</h1>
          <Button 
            onClick={() => setShowCreateLabel(true)}
            className="bg-amber-600 hover:bg-amber-700"
          >
            <FileText className="h-4 w-4 mr-2" />
            Create Label
          </Button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="p-3 bg-green-50 border border-green-200 rounded flex items-center gap-2 text-green-700">
            <CheckCircle className="h-4 w-4" />
            {success}
          </div>
        )}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded flex items-center gap-2 text-red-700">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {/* Create Label Form */}
        {showCreateLabel && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Create Shipping Label</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateLabel} className="space-y-4">
                <div className="space-y-2">
                  <Label>Order ID</Label>
                  <Input
                    value={selectedOrderId}
                    onChange={(e) => setSelectedOrderId(e.target.value)}
                    placeholder="Enter order ID"
                    required
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Carrier</Label>
                    <Select
                      value={labelForm.carrier}
                      onValueChange={(v) => setLabelForm(prev => ({ ...prev, carrier: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="usps">USPS</SelectItem>
                        <SelectItem value="ups">UPS</SelectItem>
                        <SelectItem value="fedex">FedEx</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Service Level</Label>
                    <Select
                      value={labelForm.service_level}
                      onValueChange={(v) => setLabelForm(prev => ({ ...prev, service_level: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ground">Ground</SelectItem>
                        <SelectItem value="priority">Priority</SelectItem>
                        <SelectItem value="express">Express</SelectItem>
                        <SelectItem value="overnight">Overnight</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Package Weight (lbs)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={labelForm.package_weight}
                      onChange={(e) => setLabelForm(prev => ({ ...prev, package_weight: parseFloat(e.target.value) }))}
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setShowCreateLabel(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={creating}>
                    {creating ? 'Creating...' : 'Create Label'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex gap-4">
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="label_created">Label Created</SelectItem>
              <SelectItem value="in_transit">In Transit</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
            </SelectContent>
          </Select>
          <Select value={carrierFilter || "all"} onValueChange={(v) => setCarrierFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Carriers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Carriers</SelectItem>
              <SelectItem value="USPS">USPS</SelectItem>
              <SelectItem value="UPS">UPS</SelectItem>
              <SelectItem value="FEDEX">FedEx</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Shipments List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
          </div>
        ) : shipments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Truck className="h-16 w-16 mx-auto mb-4 text-stone-300" />
              <h3 className="text-lg font-medium text-stone-800 mb-2">No shipments found</h3>
              <p className="text-stone-500">Create a shipping label to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {shipments.map((shipment) => (
              <Card key={shipment.id}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="font-semibold text-stone-800">{shipment.tracking_number}</p>
                      <p className="text-sm text-stone-500">Order: {shipment.order_number}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{shipment.carrier}</Badge>
                      <Badge className={getStatusColor(shipment.status)}>
                        {shipment.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-stone-500">Service</p>
                      <p className="font-medium text-stone-700 capitalize">{shipment.service_level}</p>
                    </div>
                    <div>
                      <p className="text-stone-500">Shipping Cost</p>
                      <p className="font-medium text-stone-700">${shipment.shipping_cost?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-stone-500">Est. Delivery</p>
                      <p className="font-medium text-stone-700">{shipment.estimated_delivery} days</p>
                    </div>
                  </div>

                  {shipment.label_url && (
                    <div className="mt-4">
                      <Button variant="outline" size="sm" asChild>
                        <a href={shipment.label_url} target="_blank" rel="noopener noreferrer">
                          <FileText className="h-4 w-4 mr-2" />
                          View Label
                        </a>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-stone-600">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminShipping;
